import { getApiHost } from "#/shared/lib/api";

/**
 * Loads the custom elements config from the script origin, then deep-merges
 * the page-level override (`window.Sparkle.ElementsConfig`, set by a layout
 * before the entrypoint runs) over it.
 *
 * @returns {Promise<Object>} The config
 * @throws {Error} If the config is not found
 */
export async function loadConfig() {
  try {
    const { apiHost } = getApiHost();
    const config = await fetchConfig(apiHost);

    return mergeConfig(config, globalThis.Sparkle?.ElementsConfig);
  } catch (cause) {
    throw new Error("Failed to load config", { cause });
  }
}

/**
 * Deep-merges the override into the base config. Plain objects merge
 * recursively; every other value (arrays included) is replaced.
 *
 * @private
 * @param {Object} base
 * @param {Object|undefined} overrides
 * @returns {Object} The merged config
 */
function mergeConfig(base, overrides) {
  const result = { ...base };

  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (value && typeof value === "object" && !Array.isArray(value) && typeof result[key] === "object") {
      result[key] = mergeConfig(result[key], value);
    } else {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Fetches the custom elements config from the script origin.
 * The request will timeout after 20 seconds.
 *
 * @private
 * @returns {Promise<Object>} The config
 * @throws {Error} If the response is not ok
 */
async function fetchConfig(apiHost) {
  const path = "/sp_components/configuration.json";
  const timeout = 20_000;
  // Without a bundle origin the path is left relative, for the document to resolve. `new URL` rejects a
  // null base rather than ignoring it, so passing the host straight through would throw here instead.
  const url = apiHost ? new URL(path, apiHost) : path;
  const signal = AbortSignal.timeout(timeout);
  const credentials = "include";
  const response = await fetch(url, { signal, credentials });

  if (response.ok) {
    return await response.json();
  }

  throw new Error("Failed to fetch config", { cause: response.statusText });
}
